import {
  oklchColor,
  oklchToHex,
  oklchToHsb,
  oklchToHsl,
  oklchToRgb,
  rgbToOklch,
  round,
  type OklchColor,
  type RgbColor,
} from "@/document/model/color";
import type { PickerMode } from "./right-panel/types";

export type ColorSampleCommit = (color: OklchColor, sampleStroke: boolean) => void;

export interface ColorSamplerRequest {
  sourceId: string;
  mode: PickerMode;
}

export type ColorSamplerRequestWithCommit = ColorSamplerRequest & {
  onSample: ColorSampleCommit;
};

export interface ColorSamplerPixel extends RgbColor {}

export interface ColorSamplePreview {
  x: number;
  y: number;
  pixels: ColorSamplerPixel[];
  color: OklchColor;
}

const SAMPLE_SIZE = 7;
const SAMPLE_RADIUS = Math.floor(SAMPLE_SIZE / 2);

export function sampleRenderedCanvas(canvas: HTMLCanvasElement, clientX: number, clientY: number): ColorSamplePreview | undefined {
  const gl = canvas.getContext("webgl2");
  if (!gl || canvas.width <= 0 || canvas.height <= 0) {
    return undefined;
  }

  const rect = canvas.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) {
    return undefined;
  }

  const centerX = Math.floor((clientX - rect.left) * canvas.width / rect.width);
  const centerY = Math.floor((clientY - rect.top) * canvas.height / rect.height);
  if (centerX < 0 || centerY < 0 || centerX >= canvas.width || centerY >= canvas.height) {
    return undefined;
  }

  const pixelBuffer = new Uint8Array(4);
  const pixels: ColorSamplerPixel[] = [];

  for (let row = -SAMPLE_RADIUS; row <= SAMPLE_RADIUS; row += 1) {
    for (let column = -SAMPLE_RADIUS; column <= SAMPLE_RADIUS; column += 1) {
      const x = clamp(centerX + column, 0, canvas.width - 1);
      const y = clamp(centerY + row, 0, canvas.height - 1);
      gl.readPixels(x, canvas.height - 1 - y, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, pixelBuffer);
      const pixel = {
        r: pixelBuffer[0] ?? 0,
        g: pixelBuffer[1] ?? 0,
        b: pixelBuffer[2] ?? 0,
        alpha: (pixelBuffer[3] ?? 255) / 255,
      };
      pixels.push(pixel);
    }
  }

  const centerPixel = pixels[SAMPLE_RADIUS * SAMPLE_SIZE + SAMPLE_RADIUS];
  if (!centerPixel) {
    return undefined;
  }

  return {
    x: clientX,
    y: clientY,
    pixels,
    color: rgbToOklch(centerPixel),
  };
}

export function colorSamplerLabel(color: OklchColor, mode: PickerMode): string {
  if (mode === "hex") {
    return oklchToHex(color).toUpperCase();
  }

  if (mode === "rgb") {
    const rgb = oklchToRgb(color);
    return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${round(rgb.alpha, 2)})`;
  }

  if (mode === "hsl") {
    const hsl = oklchToHsl(color);
    return `hsla(${round(hsl.h)}, ${round(hsl.s * 100)}%, ${round(hsl.l * 100)}%, ${round(hsl.alpha, 2)})`;
  }

  if (mode === "hsb") {
    const hsb = oklchToHsb(color);
    return `hsb(${round(hsb.h)}, ${round(hsb.s * 100)}%, ${round(hsb.b * 100)}% / ${round(hsb.alpha, 2)})`;
  }

  const normalized = oklchColor(color.l, color.c, color.h, color.alpha);
  return `oklch(${round(normalized.l * 100, 1)}% ${round(normalized.c, 4)} ${round(normalized.h, 1)} / ${round(normalized.alpha, 2)})`;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, Number.isFinite(value) ? value : min));
}
